import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/** One thing the ontology lacks for a question. */
export interface AnswerabilityMissing {
  what: 'class' | 'property' | 'domain_range' | 'other' | string;
  term?: string | null;
  detail: string;
}

/** A fact an answer needs that no data states: derived by a rule, or
 *  conditional. `rule` is the site's rule that provides it; none when no rule
 *  does, which makes it a rule required. */
export interface RuleNeed {
  kind: 'derived' | 'conditional' | string;
  fact: string;
  rule?: string | null;
}

/** One thing the question set lacks, with how many questions it holds back. */
export interface Requirement {
  what: string;
  term?: string | null;
  detail: string;
  questions: number;
}

/** The verdict on one question. `unverified`: the model's query named a term
 *  the ontology does not declare, or did not parse. `failed`: no verdict. */
export interface QuestionVerdict {
  question: string;
  verdict: 'answerable' | 'not_answerable' | 'unverified' | 'failed';
  query?: string | null;
  missing: AnswerabilityMissing[];
  needs_rules?: RuleNeed[];
  explanation: string;
  note?: string | null;
}

export interface AnswerabilityReport {
  bank_id: string;
  bank_name: string;
  model: string;
  answerable: number;
  not_answerable: number;
  unverified: number;
  failed: number;
  input_tokens: number;
  output_tokens: number;
  questions: QuestionVerdict[];
  /** Classes, properties and connections the set lacks, most questions first. */
  requirements?: Requirement[];
  /** Derived or conditional facts no rule provides, most questions first. */
  rules_required?: Requirement[];
  /** The site's rules the questions rely on. */
  rules_relied_on?: { rule: string; questions: number }[];
}

/** Ontology readiness: the share of the checked questions the ontology can
 *  answer; null with no report or an empty one. */
export function coverage(report: AnswerabilityReport | null | undefined): number | null {
  if (!report || report.questions.length === 0) return null;
  return report.answerable / report.questions.length;
}

interface CheckRun {
  started_at: string;
  started_by: string;
  bank_id: string;
  bank_name: string;
}

export interface AnswerabilityStatus {
  running: (CheckRun & { done: number; total: number; waiting_for_index: boolean }) | null;
  last: (CheckRun & { finished_at: string; report: AnswerabilityReport }) | null;
  last_failure: (CheckRun & { error: string }) | null;
}

/** The last report and the check running now; polls while one runs. */
export function useAnswerability(accessToken: string | undefined) {
  return useQuery<AnswerabilityStatus>({
    queryKey: ['answerability'],
    enabled: !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<AnswerabilityStatus>('/api/v1/answerability', accessToken as string);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false),
  });
}

/** Start a check of one bank. Accepted at once; the status says how it goes. */
export async function runAnswerability(bankId: string, accessToken: string) {
  return apiFetch<AnswerabilityStatus>('/api/v1/answerability/run', accessToken, {
    method: 'POST',
    body: JSON.stringify({ bank_id: bankId }),
  });
}
